import { useState, useCallback } from "react";
import type { Language } from "../types";
import {STATION_SCREEN_VIEWS_REPLACE_BY_CITY} from "./constants";
import {getUserCity} from "./user";
import {APIPagesName} from "./hook/use-page-data";
import {createSocket} from "node:dgram";

export type ImageState = "loading" | "loaded" | "error";

export interface ImageLoaderHook {
    imageState: ImageState;
    currentImageIndex: number;
    preloadedImages: Set<number>;
    firstLoad: boolean;
    setCurrentImageIndex: (index: number) => void;
    loadImage: (index: number, url: string) => void;
    preloadImage: (index: number, images: string[]) => void;
    handleNext: (images: string[]) => void;
    handlePrevious: () => void;
    handleImageSelect: (index: number) => void;
}

export function useImageLoader(): ImageLoaderHook {
    const [imageState, setImageState] = useState<ImageState>("loading");
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [preloadedImages, setPreloadedImages] = useState<Set<number>>(new Set());
    const [firstLoad, setFirstLoad] = useState(true);

    // Preload images for smooth transitions
    const preloadImage = useCallback((index: number, images: string[]) => {
        if (!images[index] || preloadedImages.has(index)) return;

        const img = new Image();
        img.onload = () => {
            setPreloadedImages(prev => new Set(prev).add(index));
        };
        img.src = images[index];
    }, [preloadedImages]);

    // Load current image and adjacent images
    const loadImage = useCallback((index: number, url: string) => {
        setImageState("loading");

        if (preloadedImages.has(index)) {
            setImageState("loaded");
            setFirstLoad(false);
            return;
        }

        const img = new Image();
        img.onload = () => {
            setImageState("loaded");
            setPreloadedImages(prev => new Set(prev).add(index));
            setFirstLoad(false);
        };
        img.onerror = () => {
            setImageState("error");
        };
        img.src = url;
    }, [preloadedImages]);

    // Navigation handlers
    const handleNext = useCallback((images: string[]) => {
        if (currentImageIndex < images.length - 1) {
            const nextIndex = currentImageIndex + 1;
            setCurrentImageIndex(nextIndex);
            if (!preloadedImages.has(nextIndex)) {
                setImageState("loading");
            }
        }
    }, [currentImageIndex, preloadedImages]);

    const handlePrevious = useCallback(() => {
        if (currentImageIndex > 0) {
            const prevIndex = currentImageIndex - 1;
            setCurrentImageIndex(prevIndex);
            if (!preloadedImages.has(prevIndex)) {
                setImageState("loading");
            }

        }
    }, [currentImageIndex, preloadedImages]);

    const handleImageSelect = useCallback((index: number) => {
        setCurrentImageIndex(index);
        if (!preloadedImages.has(index)) {
            setImageState("loading");
        }
    }, [preloadedImages]);

    return {
        imageState,
        currentImageIndex,
        preloadedImages,
        firstLoad,
        setCurrentImageIndex,
        loadImage,
        preloadImage,
        handleNext,
        handlePrevious,
        handleImageSelect,
    };
}


// Standardized function to extract images from page data
export function getImagesFromPageData(pageData: any, language: string): string[] {
    if (!pageData) return [];

    let imagesData = [];
    switch (language) {
        case "it":
            imagesData = pageData.screenViewImagesUrlItalian || [];
            break;
        case "en":
            imagesData = pageData.screenViewImagesUrlEnglish || [];
            break;
        case "de":
            imagesData = pageData.screenViewImagesUrlGerman || [];
            break;
        default:
            imagesData = pageData.screenViewImagesUrlEnglish || [];
    }

    // If it's a string (JSON), parse it
    if (typeof imagesData === 'string') {
        try {
            return JSON.parse(imagesData);
        } catch (e) {
            console.error('Error parsing images data:', e);
            return [];
        }
    }

    (function applyCitySpecificImageReplacements(){
        console.log(pageData);

        const userCity = getUserCity();

        /**
         * 3️⃣ Apply localized image replacements based on the user's city and language.
         *
         * This logic dynamically modifies the default list of `imagesData`
         * depending on which city the user selected (e.g., Merano, Brunico, etc.)
         * and which page (`pageName`) they are currently on.
         *
         * The replacements come from the global constant:
         * `STATION_SCREEN_VIEWS_REPLACE_BY_CITY`
         *
         * Structure example:
         * {
         *   merano: {
         *     s_station1_A: {
         *       it: { 2: "/images/merano-it-2.png" },
         *       en: { 2: "/images/merano-en-2.png" }
         *     }
         *   }
         * }
         *
         * Steps:
         *  1️⃣ Skip if the city is "bolzano" (uses default assets, no replacements)
         *  2️⃣ Retrieve city-specific replacement map
         *  3️⃣ Locate the relevant page replacement block (e.g. s_station1_A)
         *  4️⃣ Apply language-specific replacements by overriding the target image index
         */

        if (
            userCity &&
            userCity !== "bolzano" &&
            STATION_SCREEN_VIEWS_REPLACE_BY_CITY[userCity]
        ) {
            // Get all page replacements defined for this city
            const cityReplacements = STATION_SCREEN_VIEWS_REPLACE_BY_CITY[userCity];

            // Ensure that the current page has specific replacement data
            const pageName = pageData.pageName as APIPagesName;
            const pageReplacement = cityReplacements?.[pageName];

            if (pageReplacement) {
                console.log(
                    `Found city-specific replacements for ${userCity} (${pageName})`,
                    pageReplacement
                );

                // Extract the language-specific replacement set
                const langReplacement = pageReplacement[language as keyof typeof pageReplacement];

                // Apply each replacement: override the image at the specified index
                if (langReplacement) {
                    for (const [indexString, replacementValue] of Object.entries(langReplacement)) {
                        const index = parseInt(indexString, 10);

                        // Replace the image only if the index is valid within the array
                        if (!isNaN(index) && index < imagesData.length) {
                            imagesData[index] = replacementValue;
                        }
                    }
                }
            }
        }

    })();

    // If it's already an array, return it
    return Array.isArray(imagesData) ? imagesData : [];
}

// Standardized function to extract hint images from page data
export function getHintImagesFromPageData(pageData: any, language: Language): string[] {
    if (!pageData) return [];

    let hintsData = [];
    if (language === "it") {
        hintsData = pageData.hintsImagesUrlItalian || [];
    } else if (language === "de") {
        hintsData = pageData.hintsImagesUrlGerman || [];
    } else {
        hintsData = pageData.hintsImagesUrlEnglish || [];
    }

    // If it's a string (JSON), parse it
    if (typeof hintsData === 'string') {
        try {
            return JSON.parse(hintsData);
        } catch (e) {
            console.error('Error parsing hints data:', e);
            return [];
        }
    }

    // If it's already an array, return it
    return Array.isArray(hintsData) ? hintsData : [];
}